"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthProfile } from "@/lib/auth";
import {
  clearImpersonation,
  getImpersonation,
  IMPERSONATION_CHANGED_EVENT,
  notifyImpersonationChanged,
  startImpersonation,
  type RoleImpersonation,
} from "@/lib/role-impersonation";

type AuthContextValue = {
  /** Real signed-in Administrator (or other) profile from /admin/me. */
  realProfile: AuthProfile;
  /** Effective profile for UI gating (may be impersonation overlay). */
  effectiveProfile: AuthProfile;
  impersonation: RoleImpersonation | null;
  beginImpersonation: (role: {
    roleId: string;
    name: string;
    permissions?: string[];
    allowedAgentIds?: string[];
  }) => void;
  exitImpersonation: () => void;
};

const AuthProfileContext = createContext<AuthContextValue | null>(null);

function applyImpersonation(
  real: AuthProfile,
  imp: RoleImpersonation | null,
): AuthProfile {
  if (!imp) return real;
  return {
    ...real,
    roleId: imp.roleId,
    roleName: imp.roleName,
    permissions: [...imp.permissions],
    allowedAgentIds: [...imp.allowedAgentIds],
  };
}

export function AuthProfileProvider({
  profile,
  children,
}: {
  profile: AuthProfile;
  children: ReactNode;
}) {
  const [impersonation, setImpersonation] = useState<RoleImpersonation | null>(() =>
    getImpersonation(),
  );

  useEffect(() => {
    const sync = () => setImpersonation(getImpersonation());
    window.addEventListener(IMPERSONATION_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(IMPERSONATION_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const beginImpersonation = useCallback(
    (role: {
      roleId: string;
      name: string;
      permissions?: string[];
      allowedAgentIds?: string[];
    }) => {
      const next = startImpersonation(role);
      setImpersonation(next);
      notifyImpersonationChanged();
    },
    [],
  );

  const exitImpersonation = useCallback(() => {
    clearImpersonation();
    setImpersonation(null);
    notifyImpersonationChanged();
  }, []);

  const effectiveProfile = useMemo(
    () => applyImpersonation(profile, impersonation),
    [profile, impersonation],
  );

  const value = useMemo(
    () => ({
      realProfile: profile,
      effectiveProfile,
      impersonation,
      beginImpersonation,
      exitImpersonation,
    }),
    [profile, effectiveProfile, impersonation, beginImpersonation, exitImpersonation],
  );

  return (
    <AuthProfileContext.Provider value={value}>
      {children}
    </AuthProfileContext.Provider>
  );
}

/** Effective profile (impersonation overlay when active). */
export function useAuthProfile(): AuthProfile | null {
  return useContext(AuthProfileContext)?.effectiveProfile ?? null;
}

export function useRealAuthProfile(): AuthProfile | null {
  return useContext(AuthProfileContext)?.realProfile ?? null;
}

export function useRoleImpersonation() {
  const ctx = useContext(AuthProfileContext);
  return {
    impersonation: ctx?.impersonation ?? null,
    beginImpersonation: ctx?.beginImpersonation,
    exitImpersonation: ctx?.exitImpersonation,
    realProfile: ctx?.realProfile ?? null,
  };
}
